import type { AppMessages } from "@/i18n/ja";
import {
  formatForContext,
  getCurrentNotation,
} from "@/features/hoverIdle/resources/numberFormat";
import type { HugeNumber } from "@/shared/numbers";
import type { HovSkillId } from "./hovSkillDefinitions";
import {
  HOV_1_GAIN_MULTIPLIER_SKILL_BONUS,
  HOV_AP_BOAR_EXPONENT_BONUS,
  HOV_BHG_TO_HG_POWER_EXPONENT_SKILL_BONUS,
  HOV_HG_HNG_SIMULTANEOUS_PURCHASE_COUNT_BASE_SKILL_BONUS,
  HOV_HNG_RANK_SCALE_BASE_SKILL_BONUS,
  HOV_MP_CAPACITY_SECOND_SKILL_BONUS,
  HOV_MP_CAPACITY_SKILL_BONUS,
  HOV_RA_PLUM_AMPLIFIER_GAIN_MULTIPLIER,
  HOV_VARIETY_PACK_GROWTH_EXPONENT_BONUS,
} from "./hovSkillEffects";

export type HovSkillInfluenceOperator = "+" | "x";

export interface HovSkillInfluenceSpec {
  readonly operator: HovSkillInfluenceOperator;
  readonly value: string;
}

export interface HovSkillTooltipSpec {
  readonly title: string;
  readonly description: string;
  readonly influence: HovSkillInfluenceSpec | null;
}

export interface HovSkillTooltipContext {
  readonly amplifierGainOHovMultiplier: HugeNumber;
  readonly ohovHovCoreGainMultiplier: HugeNumber;
  readonly ohovHovCoreGainScalingUnlocked: boolean;
  readonly mpCapacityCoreOHovHovCoreGainMultiplier: HugeNumber;
  readonly freeMpCoreOHovHovCoreGainMultiplier: HugeNumber;
  readonly raBambooMultiplier: HugeNumber;
  readonly raPineMultiplier: HugeNumber;
  // Currently resolved AP exponent bonus contributed by AP Deer. Driven by
  // the unlocked amplifier kind count.
  readonly apDeerExponentBonus: number;
  // Currently resolved AP exponent bonus contributed by AP Butterfly. Driven
  // by the current AP, with a `+0.01` floor while acquired.
  readonly apButterflyExponentBonus: number;
}

export function buildHovSkillTooltipSpec(
  id: HovSkillId,
  messages: AppMessages["hovSkill"],
  context: HovSkillTooltipContext,
): HovSkillTooltipSpec {
  return {
    title: messages.names[id],
    description: resolveDescription(id, messages, context),
    influence: resolveInfluence(id, context),
  };
}

export function formatHovSkillInfluenceText(
  influence: HovSkillInfluenceSpec,
): string {
  return `${influence.operator}${influence.value}`;
}

function resolveDescription(
  id: HovSkillId,
  messages: AppMessages["hovSkill"],
  context: HovSkillTooltipContext,
): string {
  const descriptions = messages.descriptions;
  if (id === "ohov-hov-core-gain-multiplier-bonus") {
    const entry = descriptions["ohov-hov-core-gain-multiplier-bonus"];
    return context.ohovHovCoreGainScalingUnlocked ? entry.scaling : entry.base;
  }
  return descriptions[id];
}

function resolveInfluence(
  id: HovSkillId,
  context: HovSkillTooltipContext,
): HovSkillInfluenceSpec | null {
  switch (id) {
    case "hg-exponent-bonus":
      return {
        operator: "+",
        value: String(HOV_BHG_TO_HG_POWER_EXPONENT_SKILL_BONUS),
      };
    case "mp-capacity-bonus":
      return { operator: "+", value: String(HOV_MP_CAPACITY_SKILL_BONUS) };
    case "mp-capacity-second-bonus":
      return {
        operator: "+",
        value: String(HOV_MP_CAPACITY_SECOND_SKILL_BONUS),
      };
    case "hg-hng-simultaneous-purchase-count-base-bonus":
      return {
        operator: "+",
        value: String(HOV_HG_HNG_SIMULTANEOUS_PURCHASE_COUNT_BASE_SKILL_BONUS),
      };
    case "hov-1-gain-multiplier-bonus":
      return {
        operator: "x",
        value: String(HOV_1_GAIN_MULTIPLIER_SKILL_BONUS),
      };
    case "hng-rank-scale-base-bonus":
      return {
        operator: "+",
        value: String(HOV_HNG_RANK_SCALE_BASE_SKILL_BONUS),
      };
    case "variety-pack":
      return {
        operator: "+",
        value: String(HOV_VARIETY_PACK_GROWTH_EXPONENT_BONUS),
      };
    case "amplifier-gain-ohov-multiplier-bonus":
      return {
        operator: "x",
        value: formatForContext(context.amplifierGainOHovMultiplier, "tooltip"),
      };
    case "ohov-hov-core-gain-multiplier-bonus":
      return {
        operator: "x",
        value: formatForContext(context.ohovHovCoreGainMultiplier, "tooltip"),
      };
    case "mp-capacity-core":
      return {
        operator: "x",
        value: formatForContext(
          context.mpCapacityCoreOHovHovCoreGainMultiplier,
          "tooltip",
        ),
      };
    case "free-mp-core":
      return {
        operator: "x",
        value: formatForContext(
          context.freeMpCoreOHovHovCoreGainMultiplier,
          "tooltip",
        ),
      };
    case "ra-plum-amplifier-gain-multiplier-bonus":
      return {
        operator: "x",
        value: String(HOV_RA_PLUM_AMPLIFIER_GAIN_MULTIPLIER),
      };
    case "ra-bamboo-amplifier-gain-multiplier-bonus":
      return {
        operator: "x",
        value: formatForContext(context.raBambooMultiplier, "tooltip"),
      };
    case "ra-pine-amplifier-gain-multiplier-bonus":
      return {
        operator: "x",
        value: formatForContext(context.raPineMultiplier, "tooltip"),
      };
    case "ap-boar-exponent-bonus":
      return {
        operator: "+",
        value: formatApExponentBonus(HOV_AP_BOAR_EXPONENT_BONUS),
      };
    case "ap-deer-exponent-bonus":
      return {
        operator: "+",
        value: formatApExponentBonus(context.apDeerExponentBonus),
      };
    case "ap-butterfly-exponent-bonus":
      return {
        operator: "+",
        value: formatApExponentBonus(context.apButterflyExponentBonus),
      };
    case "amplifier-unlock":
    case "mhov-mp-control-unlock":
    case "nhov-mp-control-unlock":
    case "amplifier-panel-mp-placement-unlock":
    case "wall-unlock":
    case "wall-cap-relaxation":
    case "exploration-unlock":
      return null;
  }
}

function formatApExponentBonus(value: number): string {
  if (!Number.isFinite(value)) return "—";
  if (Math.abs(value) < 1000) {
    return trimTrailingZeros(value.toFixed(2));
  }
  return formatForContext(value, "tooltip", getCurrentNotation(), {
    decimals: 2,
    trimTrailingZeros: true,
  });
}

function trimTrailingZeros(value: string): string {
  if (!value.includes(".")) return value;
  return value.replace(/\.?0+$/, "");
}
